import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI, vansAPI, mfaAPI, warehouseAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Switch } from '../components/ui/switch';
import { PasswordInput } from '../components/ui/password-input';
import { Badge } from '../components/ui/badge';
import { AppDrawer } from '../components/ui/AppDrawer';
import {
  Plus, Users, Loader2, Truck, Edit2, X, Save, Key, Shield,
  Wrench, Settings, Archive, ArchiveRestore, ShieldCheck, ShieldAlert,
  Building2, Camera, Link2, Upload, User as UserIcon, Bell, Phone, Mail, Trash2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { MobileFAB } from '../components/MobileFAB';
import { CreateVehicleDialog } from '../components/CreateVehicleDialog';

/* ─── Avatar ─── */
const UserAvatar = ({ member, size = "md", onClick }) => {
  const [imgError, setImgError] = useState(false);
  const sizes = { sm: "w-8 h-8 text-sm", md: "w-10 h-10 text-base", lg: "w-24 h-24 text-3xl" };
  const hasPhoto = member.photo_url && !imgError;
  const photoSrc = hasPhoto
    ? member.photo_url.startsWith('http') ? member.photo_url : `${process.env.REACT_APP_BACKEND_URL}${member.photo_url}`
    : null;

  return (
    <div
      className={`${sizes[size]} bg-primary/10 rounded-full flex items-center justify-center overflow-hidden shrink-0 ${onClick ? 'cursor-pointer hover:ring-2 hover:ring-primary/40 transition-all' : ''}`}
      onClick={onClick}
    >
      {photoSrc ? (
        <img src={photoSrc} alt={member.name} className="w-full h-full object-cover" onError={() => setImgError(true)} />
      ) : (
        <span className="font-bold text-primary">{member.name?.charAt(0).toUpperCase()}</span>
      )}
    </div>
  );
};

/* ─── Role badge ─── */
const RoleBadge = ({ role }) => {
  const styles = {
    owner: 'bg-primary/10 text-primary dark:bg-primary/20',
    office: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    tech: 'bg-muted text-muted-foreground',
  };
  const labels = { owner: 'Owner', office: 'Office', tech: 'Tech' };
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${styles[role] || styles.tech}`} data-testid="role-badge">
      {labels[role] || role}
    </span>
  );
};

/* ─── Photo Upload Widget ─── */
const PhotoUpload = ({ member, editData, setEditData, onPhotoUploaded }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlMode, setUrlMode] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
      toast.error('Supported formats: JPG, PNG, WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }
    setUploading(true);
    try {
      const res = await usersAPI.uploadPhoto(member.id, file);
      const newUrl = res.data.photo_url;
      setEditData(prev => ({ ...prev, photo_url: newUrl }));
      if (onPhotoUploaded) onPhotoUploaded(newUrl);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const previewUrl = editData.photo_url
    ? editData.photo_url.startsWith('http') ? editData.photo_url : `${process.env.REACT_APP_BACKEND_URL}${editData.photo_url}`
    : null;

  return (
    <div className="flex flex-col items-center gap-3" data-testid="photo-upload-widget">
      <div className="relative group">
        <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center overflow-hidden border-2 border-border">
          {previewUrl ? (
            <img src={previewUrl} alt={member.name} className="w-full h-full object-cover" />
          ) : (
            <UserIcon className="w-10 h-10 text-muted-foreground/40" />
          )}
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
        >
          {uploading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
        </button>
        <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={handleFileUpload} />
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" className="text-xs h-7" onClick={() => fileRef.current?.click()} disabled={uploading}>
          <Upload className="w-3 h-3 mr-1" /> {uploading ? 'Uploading...' : 'Upload'}
        </Button>
        <Button type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={() => setUrlMode(!urlMode)}>
          <Link2 className="w-3 h-3 mr-1" /> URL
        </Button>
        {editData.photo_url && (
          <Button type="button" variant="ghost" size="sm" className="text-xs h-7 text-destructive" onClick={() => setEditData(prev => ({ ...prev, photo_url: '' }))}>
            <X className="w-3 h-3 mr-1" /> Remove
          </Button>
        )}
      </div>
      {urlMode && (
        <Input
          value={editData.photo_url || ''}
          onChange={(e) => setEditData(prev => ({ ...prev, photo_url: e.target.value }))}
          placeholder="https://example.com/photo.jpg"
          className="text-xs h-8"
          data-testid="edit-photo-url-input"
        />
      )}
    </div>
  );
};

/* ─── Edit Drawer ─── */
const EditDrawer = ({ open, onOpenChange, member, vans, onSave, saving, onCreateVehicle }) => {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (member && open) {
      setForm({
        name: member.name || '',
        assigned_van_id: member.assigned_van_id || null,
        po_suffix: member.po_suffix || '',
        photo_url: member.photo_url || '',
        bio: member.bio || '',
        phone: member.phone || '',
        is_active: member.is_active,
        po_spend_limit: member.po_spend_limit_cents != null ? (member.po_spend_limit_cents / 100).toFixed(2) : '',
        hourly_rate: member.hourly_rate_cents != null ? (member.hourly_rate_cents / 100).toFixed(2) : '',
        commissions_enabled: member.commissions_enabled || false,
        default_commission_rate: member.default_commission_rate != null ? String(member.default_commission_rate) : '',
        notification_prefs: member.notification_prefs || { in_app: true, sms: false, email: true },
      });
    }
  }, [member, open]);

  if (!member) return null;

  const handleSave = () => {
    const spendLimitCents = form.po_spend_limit !== '' && form.po_spend_limit != null
      ? Math.round(parseFloat(form.po_spend_limit) * 100) : null;
    const hourlyRateCents = form.hourly_rate !== '' && form.hourly_rate != null
      ? Math.round(parseFloat(form.hourly_rate) * 100) : null;
    onSave(member.id, {
      name: form.name,
      assigned_van_id: form.assigned_van_id || null,
      po_suffix: form.po_suffix || null,
      photo_url: form.photo_url || null,
      bio: form.bio || null,
      phone: form.phone || null,
      is_active: form.is_active,
      po_spend_limit_cents: spendLimitCents,
      hourly_rate_cents: hourlyRateCents,
      commissions_enabled: form.commissions_enabled,
      default_commission_rate: form.commissions_enabled && form.default_commission_rate !== ''
        ? parseFloat(form.default_commission_rate) : null,
      notification_prefs: form.notification_prefs,
    });
  };

  return (
    <AppDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={`Edit ${member.name}`}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-edit-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </>
      }
    >
      <div className="space-y-6 p-1">
        {/* ── Identity & Media ── */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Profile</h4>
          <PhotoUpload
            member={member}
            editData={form}
            setEditData={setForm}
            onPhotoUploaded={(url) => setForm(prev => ({ ...prev, photo_url: url }))}
          />
          <div className="mt-4 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Name</Label>
              <Input value={form.name || ''} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} data-testid="edit-user-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Bio</Label>
              <Textarea
                value={form.bio || ''}
                onChange={(e) => setForm(p => ({ ...p, bio: e.target.value }))}
                placeholder="Brief bio shown to customers (e.g. experience, certifications)"
                rows={3}
                className="text-sm resize-none"
                data-testid="edit-user-bio"
              />
              <p className="text-[11px] text-muted-foreground">Displayed to customers when this tech is assigned.</p>
            </div>
          </div>
        </div>

        <hr className="border-border" />

        {/* ── Operational ── */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Operations</h4>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Assigned Vehicle</Label>
              <SearchableSelect
                options={[
                  { value: 'none', label: 'Unassigned' },
                  ...vans.filter(v => v.is_active && !v.archived_at).map(v => ({ value: v.id, label: v.name }))
                ]}
                value={form.assigned_van_id ?? 'none'}
                onValueChange={(val) => setForm(p => ({ ...p, assigned_van_id: val === 'none' ? null : val }))}
                placeholder="Unassigned"
                emptyText="No vehicles found."
                onCreateNew={onCreateVehicle ? (text) => onCreateVehicle(text, (newId) => setForm(p => ({ ...p, assigned_van_id: newId }))) : undefined}
                createLabel={(text) => text ? `Create "${text}"` : '+ Create New Vehicle'}
                data-testid="edit-user-van"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">PO Suffix</Label>
              <Input
                value={form.po_suffix || ''}
                onChange={(e) => {
                  const v = e.target.value.toUpperCase();
                  if (v === '' || /^[A-Z0-9]{1,2}$/.test(v)) setForm(p => ({ ...p, po_suffix: v }));
                }}
                placeholder="e.g. A1"
                className="w-24"
                maxLength={2}
                disabled={member.role === 'owner' && member.po_suffix === '01'}
                data-testid="edit-user-po-suffix"
              />
              <p className="text-[11px] text-muted-foreground">1-2 characters for PO numbering</p>
            </div>
          </div>
        </div>

        <hr className="border-border" />

        {/* ── Financial ── */}
        <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Financial</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">PO Spend Limit</Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    type="number" step="0.01" min="0"
                    placeholder="Unlimited"
                    value={form.po_spend_limit || ''}
                    onChange={(e) => setForm(p => ({ ...p, po_spend_limit: e.target.value }))}
                    className="pl-6"
                    data-testid="edit-spend-limit-input"
                  />
                </div>
                <p className="text-[11px] text-muted-foreground">Blank = unlimited</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Hourly Rate</Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    type="number" step="0.01" min="0"
                    placeholder="Not set"
                    value={form.hourly_rate || ''}
                    onChange={(e) => setForm(p => ({ ...p, hourly_rate: e.target.value }))}
                    className="pl-6"
                    data-testid="edit-hourly-rate-input"
                  />
                </div>
                <p className="text-[11px] text-muted-foreground">Per hour for job costing</p>
              </div>
            </div>
            {/* Commission settings */}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-foreground">Enable Commissions</p>
                  <p className="text-[11px] text-muted-foreground">Track sales commissions for this team member</p>
                </div>
                <Switch
                  checked={form.commissions_enabled || false}
                  onCheckedChange={(v) => setForm(p => ({ ...p, commissions_enabled: v, ...(!v ? { default_commission_rate: '' } : {}) }))}
                  data-testid="edit-commissions-toggle"
                />
              </div>
              {form.commissions_enabled && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Default Commission Rate (%)</Label>
                  <div className="relative w-32">
                    <Input
                      type="number" step="0.5" min="0" max="100"
                      placeholder="0"
                      value={form.default_commission_rate || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || (parseFloat(val) >= 0 && parseFloat(val) <= 100)) {
                          setForm(p => ({ ...p, default_commission_rate: val }));
                        }
                      }}
                      className="pr-7"
                      data-testid="edit-commission-rate-input"
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Pre-filled when assigned as commission recipient on estimates</p>
                </div>
              )}
            </div>
        </div>

        <hr className="border-border" />

        {/* ── Status ── */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Status</h4>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Active</p>
              <p className="text-[11px] text-muted-foreground">Inactive users cannot log in</p>
            </div>
            <Switch
              checked={form.is_active ?? true}
              onCheckedChange={(v) => setForm(p => ({ ...p, is_active: v }))}
              data-testid="edit-user-active"
            />
          </div>
        </div>

        <hr className="border-border" />

        {/* ── Notification Preferences ── */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Notifications</h4>
          <p className="text-[11px] text-muted-foreground mb-3">Controls how this team member receives dispatch and schedule alerts.</p>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Personal Phone</Label>
              <Input
                type="tel"
                value={form.phone || ''}
                onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="(555) 123-4567"
                data-testid="edit-user-phone"
              />
              <p className="text-[11px] text-muted-foreground">Used for SMS alerts (cancellations, schedule changes)</p>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-3.5 h-3.5 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">In-App</p>
              </div>
              <Switch checked={true} disabled className="opacity-60" data-testid="notif-inapp-toggle" />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">Email</p>
              </div>
              <Switch
                checked={form.notification_prefs?.email ?? true}
                onCheckedChange={(v) => setForm(p => ({ ...p, notification_prefs: { ...p.notification_prefs, email: v } }))}
                data-testid="notif-email-toggle"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">SMS</p>
              </div>
              <Switch
                checked={form.notification_prefs?.sms ?? false}
                onCheckedChange={(v) => setForm(p => ({ ...p, notification_prefs: { ...p.notification_prefs, sms: v } }))}
                data-testid="notif-sms-toggle"
              />
              {form.notification_prefs?.sms && !form.phone && (
                <span className="text-[11px] text-amber-600 font-medium">Add phone above</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppDrawer>
  );
};

/* ─── Team Member Row ─── */
const MemberRow = ({ member, vans, isOwner, hasPerm, tenantSecurity, onEdit, onResetPassword, onArchive, onHardDelete, onToggleMFA, mfaLoading }) => {
  const vanName = member.assigned_van_id
    ? (vans.find(v => v.id === member.assigned_van_id)?.name || 'Unknown')
    : null;

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors ${member.archived_at ? 'opacity-60' : ''} ${!member.is_active && !member.archived_at ? 'opacity-50' : ''}`}
      data-testid={`user-row-${member.id}`}
    >
      {/* Avatar */}
      <UserAvatar member={member} size="md" />

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-foreground truncate">{member.name}</span>
          <RoleBadge role={member.role} />
          {member.archived_at && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800">Archived</Badge>}
          {!member.archived_at && !member.is_active && <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/20">Inactive</Badge>}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground flex-wrap">
          {member.email && <span className="truncate">{member.email}</span>}
          {vanName && (
            <>
              <span className="text-muted-foreground/40">|</span>
              <span className="inline-flex items-center gap-1"><Truck className="w-3 h-3" />{vanName}</span>
            </>
          )}
          {member.po_suffix && (
            <>
              <span className="text-muted-foreground/40">|</span>
              <span>PO: {member.po_suffix}</span>
            </>
          )}
        </div>
      </div>

      {/* Right-side metadata badges */}
      <div className="hidden md:flex items-center gap-2 shrink-0">
        {member.role !== 'owner' && member.po_spend_limit_cents != null && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400" data-testid={`limit-badge-${member.id}`}>
            Limit: ${(member.po_spend_limit_cents / 100).toFixed(0)}
          </span>
        )}
        {(isOwner || hasPerm('team.manage')) && member.hourly_rate_cents != null && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400" data-testid={`rate-badge-${member.id}`}>
            ${(member.hourly_rate_cents / 100).toFixed(2)}/hr
          </span>
        )}
        {!member.archived_at && member.mfa_enabled && (
          <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800 gap-0.5">
            <ShieldCheck className="w-3 h-3" /> MFA
          </Badge>
        )}
        {!member.archived_at && !member.mfa_enabled && (member.mfa_required || tenantSecurity?.mfa_required_all) && (
          <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800 gap-0.5">
            <ShieldAlert className="w-3 h-3" /> MFA Req
          </Badge>
        )}
        {isOwner && !member.archived_at && !tenantSecurity?.mfa_required_all && (
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <span>MFA</span>
            <Switch
              checked={member.mfa_required || false}
              onCheckedChange={(v) => onToggleMFA(member.id, v)}
              disabled={mfaLoading === member.id}
              className="scale-[0.65]"
              data-testid={`mfa-toggle-${member.id}`}
            />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-0.5 shrink-0">
        {!member.archived_at && (
          <>
            {member.role !== 'owner' && (
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onResetPassword(member.id)} title="Reset Password" data-testid={`reset-pwd-${member.id}`}>
                <Key className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(member)} data-testid={`edit-user-${member.id}`}>
              <Edit2 className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
        {isOwner && (
          member.archived_at ? (
            <div className="flex items-center gap-0.5">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950" onClick={() => onArchive(member, 'restore')} title="Restore" data-testid={`restore-user-${member.id}`}>
                <ArchiveRestore className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950" onClick={() => onHardDelete(member)} title="Permanently Remove" data-testid={`hard-delete-user-${member.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ) : (
            <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950" onClick={() => onArchive(member, 'archive')} title="Archive" data-testid={`archive-user-${member.id}`}>
              <Archive className="w-3.5 h-3.5" />
            </Button>
          )
        )}
      </div>
    </div>
  );
};


/* ════════════════════════════════════════════════
   Main Team Page
   ════════════════════════════════════════════════ */
export const Team = () => {
  const navigate = useNavigate();
  const { isOwner, hasPerm } = useAuth();
  const [users, setUsers] = useState([]);
  const [vans, setVans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  // Add User drawer
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', username: '', password: '', role: 'tech', assigned_van_id: null, po_spend_limit: '', hourly_rate: '', commissions_enabled: false, default_commission_rate: '' });
  const [addSaving, setAddSaving] = useState(false);

  // Edit drawer
  const [editMember, setEditMember] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  // Password reset
  const [pwdResetId, setPwdResetId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);

  // Archive confirm
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveFutureJobs, setArchiveFutureJobs] = useState(null); // {user, events, event_count}
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [hardDeleteLoading, setHardDeleteLoading] = useState(false);
  const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState('');

  // Seat limit
  const [seatInfo, setSeatInfo] = useState(null); // { current_count, limit, can_add }

  // Inline vehicle creation (from Add Member drawer)
  const [createVehicleOpen, setCreateVehicleOpen] = useState(false);
  const [vehiclePrefillName, setVehiclePrefillName] = useState('');
  // Track which form context: 'add' or 'edit'
  const [vehicleCreateCtx, setVehicleCreateCtx] = useState('add');

  // MFA / Security
  const [tenantSecurity, setTenantSecurity] = useState(null);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [mfaUserLoading, setMfaUserLoading] = useState(null);

  /* ── Data fetching ── */
  const fetchData = useCallback(async () => {
    try {
      const [usersRes, vansRes] = await Promise.all([
        usersAPI.list({ archived: showArchived }),
        vansAPI.list(),
      ]);
      setUsers(usersRes.data);
      setVans(vansRes.data);
      // Fetch seat info
      try {
        const planRes = await warehouseAPI.getPlanLimits();
        if (planRes.data?.user) setSeatInfo(planRes.data.user);
      } catch {}
      if (isOwner) {
        try { setTenantSecurity((await mfaAPI.getTenantSecurity()).data); } catch {}
      }
    } catch { toast.error('Failed to load team data'); }
    finally { setLoading(false); }
  }, [showArchived, isOwner]);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

  const seatAtLimit = seatInfo?.limit != null && !seatInfo.can_add;
  const seatLabel = seatInfo?.limit != null ? `${seatInfo.current_count}/${seatInfo.limit} seats` : null;

  /* ── Inline vehicle creation (from SearchableSelect) ── */
  const editVanSetterRef = React.useRef(null);

  const handleCreateVehicle = (searchText, ctx = 'add') => {
    setVehiclePrefillName(searchText);
    setVehicleCreateCtx(ctx);
    setCreateVehicleOpen(true);
  };

  const handleCreateVehicleFromEdit = (searchText, setterFn) => {
    editVanSetterRef.current = setterFn;
    setVehiclePrefillName(searchText);
    setVehicleCreateCtx('edit');
    setCreateVehicleOpen(true);
  };

  const handleVehicleCreated = async (newVehicle) => {
    // Refresh vans list
    try {
      const vansRes = await vansAPI.list();
      setVans(vansRes.data);
    } catch {}
    // Auto-select the new vehicle in the correct form
    if (newVehicle?.id) {
      if (vehicleCreateCtx === 'add') {
        setAddForm(p => ({ ...p, assigned_van_id: newVehicle.id }));
      } else if (vehicleCreateCtx === 'edit' && editVanSetterRef.current) {
        editVanSetterRef.current(newVehicle.id);
        editVanSetterRef.current = null;
      }
    }
  };

  /* ── Add user ── */
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addForm.email && !addForm.username) { toast.error('Email or username is required'); return; }
    if (addForm.username) {
      if (addForm.username.startsWith('.') || addForm.username.endsWith('.')) { toast.error('Username cannot start or end with a period'); return; }
      if (!/^[a-z0-9._-]+$/.test(addForm.username)) { toast.error('Username can only contain letters, numbers, periods, underscores, and hyphens'); return; }
    }
    setAddSaving(true);
    try {
      const spendCents = addForm.po_spend_limit !== '' ? Math.round(parseFloat(addForm.po_spend_limit) * 100) : null;
      const hourlyRateCents = addForm.hourly_rate !== '' ? Math.round(parseFloat(addForm.hourly_rate) * 100) : null;
      await usersAPI.create({
        name: addForm.name, email: addForm.email || null, username: addForm.username || null,
        password: addForm.password, role: addForm.role, assigned_van_id: addForm.assigned_van_id || null,
        po_spend_limit_cents: spendCents,
        hourly_rate_cents: hourlyRateCents,
        commissions_enabled: addForm.commissions_enabled,
        default_commission_rate: addForm.commissions_enabled && addForm.default_commission_rate !== '' ? parseFloat(addForm.default_commission_rate) : null,
      });
      toast.success('Team member added');
      setAddOpen(false);
      setAddForm({ name: '', email: '', username: '', password: '', role: 'tech', assigned_van_id: null, po_spend_limit: '', hourly_rate: '', commissions_enabled: false, default_commission_rate: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add user'); }
    finally { setAddSaving(false); }
  };

  /* ── Edit user ── */
  const handleSaveEdit = async (userId, data) => {
    setEditSaving(true);
    try {
      await usersAPI.update(userId, data);
      toast.success('User updated');
      setEditOpen(false);
      setEditMember(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update user'); }
    finally { setEditSaving(false); }
  };

  /* ── Password reset ── */
  const handlePwdReset = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error('Min 6 characters'); return; }
    setPwdSaving(true);
    try {
      await usersAPI.update(pwdResetId, { password: newPassword });
      toast.success('Password reset');
      setPwdResetId(null);
      setNewPassword('');
    } catch { toast.error('Failed to reset password'); }
    finally { setPwdSaving(false); }
  };

  /* ── Archive / Restore ── */
  const handleArchiveAction = async (forceUnassign = false) => {
    if (!archiveTarget) return;
    setArchiveLoading(true);
    try {
      if (archiveTarget.action === 'archive') await usersAPI.archive(archiveTarget.user.id, forceUnassign);
      else await usersAPI.restore(archiveTarget.user.id);
      toast.success(`${archiveTarget.user.name} ${archiveTarget.action === 'archive' ? 'archived' : 'restored'}${forceUnassign ? ' (dispatch events unassigned)' : ''}`);
      setArchiveTarget(null);
      setArchiveFutureJobs(null);
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Handle 409: future jobs assigned
      if (err.response?.status === 409 && detail?.code === 'future_jobs_assigned') {
        setArchiveFutureJobs({
          user: archiveTarget.user,
          event_count: detail.event_count,
          events: detail.events || [],
          message: detail.message,
        });
        setArchiveTarget(null);
      } else {
        toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed');
      }
    }
    finally { setArchiveLoading(false); }
  };

  const handleForceArchive = async () => {
    if (!archiveFutureJobs) return;
    setArchiveLoading(true);
    try {
      await usersAPI.archive(archiveFutureJobs.user.id, true);
      toast.success(`${archiveFutureJobs.user.name} archived (${archiveFutureJobs.event_count} dispatch event(s) unassigned)`);
      setArchiveFutureJobs(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to archive');
    }
    finally { setArchiveLoading(false); }
  };

  const handleHardDelete = async () => {
    if (!hardDeleteTarget || hardDeleteConfirmText !== 'DELETE') return;
    setHardDeleteLoading(true);
    try {
      await usersAPI.hardDelete(hardDeleteTarget.id);
      toast.success('Team member permanently removed. Historical records preserved as "Former Team Member".');
      setHardDeleteTarget(null);
      setHardDeleteConfirmText('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
    finally { setHardDeleteLoading(false); }
  };

  /* ── MFA ── */
  const handleToggleTenantMFA = async (enabled) => {
    setSecurityLoading(true);
    try {
      await mfaAPI.setTenantSecurity(enabled);
      setTenantSecurity(p => ({ ...p, mfa_required_all: enabled }));
      toast.success(enabled ? 'MFA required for all users' : 'Tenant MFA requirement disabled');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSecurityLoading(false); }
  };

  const handleToggleUserMFA = async (userId, required) => {
    setMfaUserLoading(userId);
    try {
      await mfaAPI.setUserRequired(userId, required);
      setUsers(p => p.map(u => u.id === userId ? { ...u, mfa_required: required } : u));
      toast.success(required ? 'MFA required' : 'MFA requirement removed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setMfaUserLoading(null); }
  };

  /* ═══════════════════ Render ═══════════════════ */
  return (
    <Layout>
      <div className="space-y-5 pb-20 md:pb-0" data-testid="team-page">
        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Team</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Manage owners, staff, and technicians
              {seatLabel && (
                <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${seatAtLimit ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-muted text-muted-foreground'}`} data-testid="seat-counter">
                  {seatLabel}
                </span>
              )}
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2">
            {isOwner && (
              <Button variant="outline" size="sm" onClick={() => navigate('/company-settings')} data-testid="company-settings-btn">
                <Building2 className="w-4 h-4 mr-1.5" /> Company Settings
              </Button>
            )}
            {isOwner && (
              <Button variant="outline" size="sm" onClick={() => navigate('/manage-access')} data-testid="manage-access-btn">
                <Settings className="w-4 h-4 mr-1.5" /> Manage Access
              </Button>
            )}
            {!showArchived && (
              seatAtLimit ? (
                <Button size="sm" variant="outline" onClick={() => navigate('/billing')} data-testid="upgrade-plan-btn" className="border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950">
                  Upgrade to add users
                </Button>
              ) : (
                <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-user-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Add User
                </Button>
              )
            )}
          </div>
        </div>

        {/* ── Security Settings (owner only) ── */}
        {isOwner && tenantSecurity && !showArchived && (
          <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Security Settings</p>
                    <p className="text-xs text-muted-foreground">
                      {tenantSecurity.mfa_enabled_count}/{tenantSecurity.total_active_users} users with MFA
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground hidden sm:inline">Require MFA for all</span>
                  <Switch
                    checked={tenantSecurity.mfa_required_all}
                    onCheckedChange={handleToggleTenantMFA}
                    disabled={securityLoading}
                    data-testid="tenant-mfa-toggle"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Tabs ── */}
        <div className="flex gap-2 border-b border-border">
          <button
            onClick={() => setShowArchived(false)}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${!showArchived ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-active-users"
          >
            Active
            {!showArchived && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
          </button>
          <button
            onClick={() => setShowArchived(true)}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${showArchived ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-archived-users"
          >
            Archived
            {showArchived && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
          </button>
        </div>

        {/* ── List ── */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : users.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              {showArchived ? (
                <>
                  <Archive className="w-10 h-10 text-muted-foreground/40 mb-3" />
                  <p className="font-semibold text-foreground">No archived users</p>
                  <p className="text-sm text-muted-foreground mt-1">Archived users will appear here</p>
                </>
              ) : (
                <>
                  <Users className="w-10 h-10 text-muted-foreground/40 mb-3" />
                  <p className="font-semibold text-foreground">No team members yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Add technicians to get started</p>
                  {!seatAtLimit && (
                    <Button className="mt-4" onClick={() => setAddOpen(true)} data-testid="create-first-user-btn">
                      <Plus className="w-4 h-4 mr-2" /> Add User
                    </Button>
                  )}
                  {seatAtLimit && (
                    <Button className="mt-4" variant="outline" onClick={() => navigate('/billing')} data-testid="upgrade-plan-empty-btn">
                      Upgrade to add users
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              {users.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  vans={vans}
                  isOwner={isOwner}
                  hasPerm={hasPerm}
                  tenantSecurity={tenantSecurity}
                  onEdit={(member) => { setEditMember(member); setEditOpen(true); }}
                  onResetPassword={(id) => setPwdResetId(id)}
                  onArchive={(user, action) => setArchiveTarget({ user, action })}
                  onHardDelete={(member) => setHardDeleteTarget(member)}
                  onToggleMFA={handleToggleUserMFA}
                  mfaLoading={mfaUserLoading}
                />
              ))}
            </CardContent>
          </Card>
        )}

        {/* ── Mobile FAB ── */}
        {!showArchived && !seatAtLimit && (
          <MobileFAB onClick={() => setAddOpen(true)} data-testid="add-user-fab" />
        )}

        {/* ── Add User Drawer ── */}
        <AppDrawer
          open={addOpen}
          onOpenChange={(open) => {
            setAddOpen(open);
            if (!open) setAddForm({ name: '', email: '', username: '', password: '', role: 'tech', assigned_van_id: null, po_spend_limit: '' });
          }}
          title="Add Team Member"
          description="Create a new owner, office staff, or technician account"
          size="md"
          isDirty={!!(addForm.name || addForm.email || addForm.username || addForm.password)}
          footer={
            <>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" form="add-user-form" disabled={addSaving} data-testid="save-user-btn">
                {addSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {addSaving ? 'Creating...' : `Add ${addForm.role === 'owner' ? 'Owner' : addForm.role === 'office' ? 'Office Staff' : 'Technician'}`}
              </Button>
            </>
          }
        >
          <form id="add-user-form" onSubmit={handleAdd} className="space-y-4 p-1">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Role *</Label>
              <SearchableSelect
                options={[
                  { value: 'owner', label: 'Owner (Full access)' },
                  { value: 'office', label: 'Office Staff (Approvals + Warehouse)' },
                  { value: 'tech', label: 'Technician (Field access)' },
                ]}
                value={addForm.role}
                onValueChange={(v) => setAddForm(p => ({ ...p, role: v }))}
                placeholder="Select role"
                sortAlphabetically={false}
                data-testid="user-role-select"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Full Name *</Label>
              <Input value={addForm.name} onChange={(e) => setAddForm(p => ({ ...p, name: e.target.value }))} required placeholder="e.g. Mike Smith" data-testid="user-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Email <span className="font-normal text-muted-foreground">(or Username)</span></Label>
              <Input type="email" value={addForm.email} onChange={(e) => setAddForm(p => ({ ...p, email: e.target.value }))} placeholder="mike@company.com" data-testid="user-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Username <span className="font-normal text-muted-foreground">(or Email)</span></Label>
              <Input
                value={addForm.username}
                onChange={(e) => {
                  let v = e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '').replace(/\.{2,}/g, '.');
                  setAddForm(p => ({ ...p, username: v }));
                }}
                placeholder="mike.johnson"
                data-testid="user-username-input"
              />
              <p className="text-[11px] text-muted-foreground">At least one of email or username required</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Temporary Password *</Label>
              <PasswordInput
                value={addForm.password}
                onChange={(e) => setAddForm(p => ({ ...p, password: e.target.value }))}
                required minLength={6}
                placeholder="Min 6 characters"
                data-testid="user-password-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Assigned Vehicle (Optional)</Label>
              <SearchableSelect
                options={[
                  { value: 'none', label: 'Unassigned' },
                  ...vans.filter(v => v.is_active && !v.archived_at).map(v => ({ value: v.id, label: v.name })),
                ]}
                value={addForm.assigned_van_id ?? 'none'}
                onValueChange={(v) => setAddForm(p => ({ ...p, assigned_van_id: v === 'none' ? null : v }))}
                placeholder="Select a vehicle"
                emptyText="No vehicles found."
                onCreateNew={(text) => handleCreateVehicle(text, 'add')}
                createLabel={(text) => text ? `Create "${text}"` : '+ Create New Vehicle'}
                data-testid="user-van-select"
              />
            </div>
            <hr className="border-border" />
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Financial</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Hourly Rate</Label>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                    <Input
                      type="number" step="0.01" min="0"
                      placeholder="Not set"
                      value={addForm.hourly_rate || ''}
                      onChange={(e) => setAddForm(p => ({ ...p, hourly_rate: e.target.value }))}
                      className="pl-6"
                      data-testid="add-hourly-rate-input"
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground">Per hour for job costing</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">PO Spend Limit</Label>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                    <Input
                      type="number" step="0.01" min="0"
                      placeholder="Unlimited"
                      value={addForm.po_spend_limit || ''}
                      onChange={(e) => setAddForm(p => ({ ...p, po_spend_limit: e.target.value }))}
                      className="pl-6"
                      data-testid="add-spend-limit-input"
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground">Blank = unlimited</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">Enable Commissions</p>
                    <p className="text-[11px] text-muted-foreground">Track sales commissions for this team member</p>
                  </div>
                  <Switch
                    checked={addForm.commissions_enabled || false}
                    onCheckedChange={(v) => setAddForm(p => ({ ...p, commissions_enabled: v, ...(!v ? { default_commission_rate: '' } : {}) }))}
                    data-testid="add-commissions-toggle"
                  />
                </div>
                {addForm.commissions_enabled && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Default Commission Rate (%)</Label>
                    <div className="relative w-32">
                      <Input
                        type="number" step="0.5" min="0" max="100"
                        placeholder="0"
                        value={addForm.default_commission_rate || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || (parseFloat(val) >= 0 && parseFloat(val) <= 100)) {
                            setAddForm(p => ({ ...p, default_commission_rate: val }));
                          }
                        }}
                        className="pr-7"
                        data-testid="add-commission-rate-input"
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Pre-filled when assigned as commission recipient on estimates</p>
                  </div>
                )}
              </div>
            </div>
          </form>
        </AppDrawer>

        {/* ── Edit Drawer ── */}
        <EditDrawer
          open={editOpen}
          onOpenChange={(open) => { setEditOpen(open); if (!open) setEditMember(null); }}
          member={editMember}
          vans={vans}
          onSave={handleSaveEdit}
          saving={editSaving}
          onCreateVehicle={handleCreateVehicleFromEdit}
        />

        {/* ── Password Reset Dialog ── */}
        <Dialog open={!!pwdResetId} onOpenChange={(open) => { if (!open) { setPwdResetId(null); setNewPassword(''); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>Set a new password for this user</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <PasswordInput
                placeholder="Min 6 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
              />
              <Button className="w-full" onClick={handlePwdReset} disabled={pwdSaving} data-testid="reset-password-btn">
                {pwdSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Reset Password
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ── Archive/Restore Dialog ── */}
        <Dialog open={!!archiveTarget} onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {archiveTarget?.action === 'archive' ? <Archive className="w-5 h-5 text-amber-600" /> : <ArchiveRestore className="w-5 h-5 text-green-600" />}
                {archiveTarget?.action === 'archive' ? 'Archive User' : 'Restore User'}
              </DialogTitle>
              <DialogDescription>
                {archiveTarget?.action === 'archive'
                  ? `Archive "${archiveTarget?.user?.name}"? They will no longer be able to log in.`
                  : `Restore "${archiveTarget?.user?.name}"? They will regain access.`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setArchiveTarget(null)} disabled={archiveLoading}>Cancel</Button>
              <Button
                className={archiveTarget?.action === 'archive' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}
                onClick={handleArchiveAction}
                disabled={archiveLoading}
                data-testid={`confirm-${archiveTarget?.action}-user`}
              >
                {archiveLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {archiveTarget?.action === 'archive' ? 'Archive' : 'Restore'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Future Jobs Warning Dialog (Archive Safety) ── */}
        <Dialog open={!!archiveFutureJobs} onOpenChange={(open) => { if (!open) setArchiveFutureJobs(null); }}>
          <DialogContent className="sm:max-w-lg" data-testid="archive-future-jobs-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                Upcoming Dispatch Events
              </DialogTitle>
              <DialogDescription className="text-left">
                <strong>{archiveFutureJobs?.user?.name}</strong> has <strong>{archiveFutureJobs?.event_count}</strong> upcoming dispatch event(s). These must be handled before archiving.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-1">
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-3 max-h-48 overflow-y-auto">
                {archiveFutureJobs?.events?.map((evt, i) => (
                  <div key={evt.event_id} className={`flex items-center justify-between py-1.5 ${i > 0 ? 'border-t border-amber-200/50 dark:border-amber-700/50' : ''}`}>
                    <span className="text-sm font-medium text-amber-900 dark:text-amber-200">{evt.job_label}</span>
                    <span className="text-xs text-amber-700 dark:text-amber-400">
                      {evt.start_at ? new Date(evt.start_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </span>
                  </div>
                ))}
                {archiveFutureJobs?.event_count > 20 && (
                  <p className="text-xs text-amber-600 mt-2">...and {archiveFutureJobs.event_count - 20} more</p>
                )}
              </div>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                <p className="text-xs text-blue-800 dark:text-blue-300">
                  <strong>Unassign & Archive</strong> will remove this person from all listed dispatch events (the events stay on the calendar but become unassigned), then archive the user.
                </p>
              </div>
            </div>
            <DialogFooter className="mt-2">
              <Button variant="outline" onClick={() => setArchiveFutureJobs(null)} disabled={archiveLoading} data-testid="cancel-archive-future-jobs">Cancel</Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700"
                onClick={handleForceArchive}
                disabled={archiveLoading}
                data-testid="confirm-unassign-archive"
              >
                {archiveLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Unassign & Archive
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Hard Delete Confirmation Dialog ── */}
        <Dialog open={!!hardDeleteTarget} onOpenChange={(open) => { if (!open) { setHardDeleteTarget(null); setHardDeleteConfirmText(''); } }}>
          <DialogContent className="sm:max-w-md" data-testid="hard-delete-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Permanently Remove Team Member
              </DialogTitle>
              <DialogDescription className="text-left">
                This will permanently remove <strong>{hardDeleteTarget?.name}</strong> from your team.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-1">
              <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-1.5">
                <p className="text-xs font-medium text-red-800">This action is irreversible:</p>
                <ul className="text-xs text-red-700 space-y-0.5 list-disc list-inside">
                  <li>Their login access will be permanently revoked</li>
                  <li>Their email will be freed — you can send a fresh invite later</li>
                  <li>Historical records (jobs, estimates, notes) will remain but show <strong>"Former Team Member"</strong></li>
                </ul>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-xs text-amber-800">
                  <strong>Use Archive instead</strong> if you just want to block access temporarily.
                  Archived users can be restored later with their identity and history intact.
                </p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Type DELETE to confirm</label>
                <Input
                  value={hardDeleteConfirmText}
                  onChange={e => setHardDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="font-mono text-sm"
                  data-testid="hard-delete-confirm-input"
                />
              </div>
            </div>
            <DialogFooter className="mt-2">
              <Button variant="outline" onClick={() => { setHardDeleteTarget(null); setHardDeleteConfirmText(''); }} disabled={hardDeleteLoading}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={handleHardDelete}
                disabled={hardDeleteLoading || hardDeleteConfirmText !== 'DELETE'}
                data-testid="confirm-hard-delete-btn"
              >
                {hardDeleteLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Permanently Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Inline Vehicle Creation Dialog */}
        <CreateVehicleDialog
          open={createVehicleOpen}
          onOpenChange={setCreateVehicleOpen}
          prefillName={vehiclePrefillName}
          onCreated={handleVehicleCreated}
        />
      </div>
    </Layout>
  );
};
